import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useImperativeHandle,
  forwardRef,
  memo,
  useMemo,
} from 'react';
import classnames from 'classnames';
import {
  faPlus,
  faMinus,
  faDownload,
  faRedo,
  faUndo,
  faFastBackward,
  faStepBackward,
  faFastForward,
  faStepForward,
  faPlusSquare,
  faLock,
  faShareSquare,
} from '@fortawesome/free-solid-svg-icons';
import { faChromecast } from '@fortawesome/free-brands-svg-icons';
import CustomViewerButton from 'features/common/CustomViewerButton';
import CustomViewerMultiActionButton from 'features/common/CustomViewerMultiActionButton';
import T from 'i18n';
import Tooltip from 'features/common/SimpleTooltip';
import { ZoomType } from '../../types';
import MouseToolSelectButtons from './MouseToolSelectButtons';
import InfoText from 'features/viewing/InfoText';
import { TooltipWrapper } from 'features/viewing/TooltipWrapper';
import PageFitmentButton from './PageFitmentButton';
import CurrentGlobalPage from './CurrentGlobalPage';
import ExhibitNumber from './ExhibitNumber';
import PageNumberControl from './PageNumberControl';
import { usePrevious } from 'utils/hooks';
import { useSelector } from 'react-redux';
import {
  selectBundleTabPage,
  selectParams,
  selectQuery,
  selectShowExhibitNumber,
  selectUseLegacyDownload,
} from 'common/selectors';
import { useToasts } from 'react-toast-notifications';
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded';
import { DEFAULT_ANNOTATION_FILTER } from 'features/viewing/redux/filterAnnotations';
import { Avatar, Badge, ClickAwayListener, Popper } from '@mui/material';
import { useDispatch } from 'react-redux';
import { filterAnnotations } from 'features/viewing/redux/fetchAnnotations';
import FilterAnnotations from 'features/case/RightSidebarTabList/FilterAnnotations';
import { FileDownload } from 'features/case/DocumentsModal/modals/FileDownload';
import LegacyFileDownload from 'features/case/DocumentsModal/modals/LegacyFileDownload';
import { urlChanged } from 'features/matcher/redux/routeChanged';
// import { getGlobalPageText } from './globalPage';
import { usePresentingTabIndicator } from '../hooks/usePresentingTabIndicator';

type AVToolboxProps = {
  viewerContainer: HTMLDivElement;
  isTheatreMode: boolean;
  isFullScreen?: boolean;
  isFileToCompare?: boolean;
  fileToCompareData?: any;
  presenter: string;
  fileId: string;
  presentMode: string | null;
  currentScaleFactor: number;
  pageCount: number;
  zoom: (type: string) => void;
  rotate: (angle: number) => void;
  mouseTool: (toolType: any) => void;
  selectedMouseTool: string;
  startPageForGlobalPaging: string;
  goToFirstPage: () => void;
  goToLastPage: () => void;
  goToPrevPage: () => void;
  goToNextPage: () => void;
  setPageNumber: (pageNumber: number) => void;
  present: (
    mode?: 'private' | 'public',
    forcePresent?: boolean,
    overrideDoc?: any,
    overrideGoToPage?: number,
  ) => void;
  permissions: any;
  privateFile?: boolean;
  showGlobalPaging: boolean;
  currentFileMetaData: any;
  isPresentModePage: boolean;
  isPreviewMode?: boolean;
  userId: string;
  isPresentViewer?: boolean;
  readOnly?: boolean;
  presentViewOverrideHandler?: any;
  viewerControlRef?: any;
  copyable?: boolean;
  hidePresentButton?: boolean;
};

export default memo(
  forwardRef(
    (
      {
        viewerContainer,
        isTheatreMode,
        isFullScreen,
        isFileToCompare,
        fileToCompareData,
        presenter,
        fileId,
        presentMode,
        currentScaleFactor,
        pageCount,
        zoom,
        rotate,
        mouseTool,
        selectedMouseTool,
        startPageForGlobalPaging,
        goToFirstPage,
        goToLastPage,
        goToPrevPage,
        goToNextPage,
        setPageNumber,
        present,
        permissions,
        privateFile,
        showGlobalPaging,
        currentFileMetaData,
        isPresentModePage,
        isPreviewMode,
        userId,
        isPresentViewer,
        readOnly,
        presentViewOverrideHandler,
        viewerControlRef,
        copyable,
        hidePresentButton,
      }: AVToolboxProps,
      ref: any,
    ) => {
      const dispatch = useDispatch();
      const [showBottomButtons, setShowBottomButtons] = useState<boolean>(false);
      const [showLeftButtons, setShowLeftButtons] = useState<boolean>(false);
      const [annotationFilters, setAnnotationFilters] = useState<any>(DEFAULT_ANNOTATION_FILTER);
      const [filterPosition, setFilterPosition] = useState<HTMLElement | null>(null);
      const [showFileVariantsModal, setShowFileVariantsModal] = useState<boolean>(false);
      const showBottomButtonsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
      const showLeftButtonsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
      const [currentPageNumber, setCurrentPageNumber] = useState<number>(1);
      const currentScaleFactorPrev = usePrevious(currentScaleFactor);
      const currentPageNumberPrev = usePrevious(currentPageNumber);
      const { addToast } = useToasts();

      const { case: aCase, file } = useSelector(selectParams) as any;
      const btpActive = useSelector(selectBundleTabPage);
      const showExhibitNumber = useSelector(selectShowExhibitNumber);
      const useLegacyDownload = useSelector(selectUseLegacyDownload);
      const DownloadComponent = useLegacyDownload ? LegacyFileDownload : FileDownload;
      const {
        goToPage: goToPageNumber,
        globalPagePrefix: globalPagePrefix,
        tab: tab,
      } = useSelector(selectQuery) as any;
      const parsedUrl = new URL(window.location.href);
      const preview = parsedUrl.searchParams.get('preview');
      const filterCount =
        Object.keys(annotationFilters).filter((key) => !annotationFilters[key]).length +
        (annotationFilters.createdBy
          ? Object.keys(annotationFilters.createdBy).filter(
              (key) => !annotationFilters.createdBy[key],
            ).length
          : 0);

      const showBottomButtonsHandler = useCallback(() => {
        if (!showBottomButtons) setShowBottomButtons(true);
        if (showBottomButtonsTimeoutRef.current) {
          clearTimeout(showBottomButtonsTimeoutRef.current);
          showBottomButtonsTimeoutRef.current = null;
        }
      }, [showBottomButtons]);

      const hideBottomButtonsHandler = () => {
        if (showBottomButtons && !showBottomButtonsTimeoutRef.current)
          showBottomButtonsTimeoutRef.current = setTimeout(() => setShowBottomButtons(false), 3000);
      };

      const showLeftButtonsHandler = useCallback(() => {
        if (!showLeftButtons) setShowLeftButtons(true);
        if (showLeftButtonsTimeoutRef.current) {
          clearTimeout(showLeftButtonsTimeoutRef.current);
          showLeftButtonsTimeoutRef.current = null;
        }
      }, [showLeftButtons]);

      const hideLeftButtonsHandler = () => {
        if (showLeftButtons && !showLeftButtonsTimeoutRef.current)
          showLeftButtonsTimeoutRef.current = setTimeout(() => setShowLeftButtons(false), 3000);
      };
      usePresentingTabIndicator({
        currentPage: currentPageNumber,
      });

      useEffect(() => {
        viewerContainer.addEventListener('mousemove', (e) => {
          const { left, top } = viewerContainer.getBoundingClientRect();
          if (
            e.clientY - top >
            Math.min(viewerContainer.clientHeight * 0.9, viewerContainer.clientHeight - 60)
          )
            showBottomButtonsHandler();
          else hideBottomButtonsHandler();

          if (e.clientX - left < Math.max(viewerContainer.clientWidth * 0.15, 80))
            showLeftButtonsHandler();
          else hideLeftButtonsHandler();
        });

        return () => {
          if (showBottomButtonsTimeoutRef.current)
            clearTimeout(showBottomButtonsTimeoutRef.current);
          if (showLeftButtonsTimeoutRef.current) clearTimeout(showLeftButtonsTimeoutRef.current);
        };
        // MOUNT
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, []);
      useEffect(() => {
        if (currentScaleFactorPrev !== currentScaleFactor) showLeftButtonsHandler();
        if (currentPageNumberPrev !== currentPageNumber) showBottomButtonsHandler();
      }, [
        currentPageNumber,
        currentPageNumberPrev,
        currentScaleFactor,
        currentScaleFactorPrev,
        showBottomButtonsHandler,
        showLeftButtonsHandler,
      ]);

      const resolvedCurrentFileMetaData = useMemo(() => {
        if (currentFileMetaData?.currentFileDetails) return currentFileMetaData.currentFileDetails;
        // this next bit is a sanity check, cross checking the URL to make sure it's all good.
        if (btpActive) {
          const updatedMetadata = { ...currentFileMetaData };
          if (tab) {
            updatedMetadata.tab = tab;
          }
          if (globalPagePrefix) {
            updatedMetadata.globalPagePrefix = globalPagePrefix;
          }
          return updatedMetadata;
        }
        return currentFileMetaData;
      }, [currentFileMetaData]);

      const copyUrlHandler = () => {
        const fileIdentifier = file || fileId;
        const shareUrl = `${window.location.origin}/case/${aCase}/files/${fileIdentifier}?goToPage=${currentPageNumber}`;

        // Extract digits after the last hyphen from file ID
        const lastHyphenIndex = fileIdentifier.lastIndexOf('-');
        const fileDigits =
          lastHyphenIndex !== -1 ? fileIdentifier.substring(lastHyphenIndex + 1) : fileIdentifier;

        // Create the display text in format {fileDigits/currentPageNumber}
        const displayText = `{${fileDigits}/${currentPageNumber}}`;

        // Create HTML with clickable link
        const htmlToCopy = `<a href="${shareUrl}" style="text-decoration: none; color: inherit;">${displayText}</a>`;

        const success = (
          <>
            <strong>{T.translate('case.fileId', { file: fileIdentifier })}</strong>
            <div>{T.translate('case.urlCoppied', { url: displayText })}</div>
          </>
        );

        const failure = (
          <>
            <strong>{T.translate('case.fileId', { file: fileIdentifier })}</strong>
            <div>{T.translate('case.urlCoppyUnsuccessfull', { url: displayText })}</div>
          </>
        );

        navigator.clipboard
          .write([
            new ClipboardItem({
              'text/html': new Blob([htmlToCopy], { type: 'text/html' }),
              'text/plain': new Blob([displayText], { type: 'text/plain' }),
            }),
          ])
          .then(
            function () {
              addToast(success, {
                appearance: 'info',
                autoDismiss: true,
              });
            },
            function () {
              addToast(failure, {
                appearance: 'error',
                autoDismiss: false,
              });
            },
          );
      };

      const filterClicked = (event: any) => {
        setFilterPosition(event.currentTarget);
      };

      const handleClickAway = () => {
        setFilterPosition(null);
      };

      const filterHandler = (val: any) => {
        setAnnotationFilters(val);
        dispatch(filterAnnotations(val, userId, !!isFileToCompare));
      };

      const isPresenting = !!presentMode;

      const shouldRenderGlobalPage = useMemo(() => {
        const gp =
          (resolvedCurrentFileMetaData && resolvedCurrentFileMetaData.globalPagePrefix) || '';
        return (
          showGlobalPaging &&
          resolvedCurrentFileMetaData &&
          Object.keys(resolvedCurrentFileMetaData).length > 0 &&
          gp.toString().trim().length > 0
        );
      }, [resolvedCurrentFileMetaData, showGlobalPaging]);

      useImperativeHandle(ref, () => ({
        pageNumberChangedHandler: ({ pageNumber }: any) => {
          if (currentPageNumber !== pageNumber) setCurrentPageNumber(pageNumber);
        },
      }));

      // TO-DO this needs to go, the whole goToPageNumber thing is a mess
      useEffect(() => {
        if (!isTheatreMode && fileId === file) {
          // we need fileId === file because if for present-view we switch file it breaks
          // so if we are changing file then skip this step, if keeping the same file then do it
          if (goToPageNumber && goToPageNumber !== currentPageNumber) {
            const localUrl = new URL(location.href);
            localUrl.searchParams.delete('goToPage');
            history.replaceState({}, '', localUrl);
            dispatch(urlChanged());
            setTimeout(() => setPageNumber(goToPageNumber), 1000); // wait for a second to get stuff ready, isViwerReady did not help
          }
        }
      }, [currentPageNumber, goToPageNumber, isTheatreMode, setPageNumber, fileId, file, dispatch]);

      const raiseBottom = !!(isFullScreen && fileToCompareData?.id);
      const dynamicBottomStyle: React.CSSProperties | undefined = raiseBottom
        ? { bottom: '4rem' }
        : undefined;
      const urlParams = new URLSearchParams(window.location.search);
      const isMainAskAI = window.location.href.includes('/ai-tools');
      const askAI = urlParams.get('askAI');
      const isFileAskAI = urlParams.get('fileAskAI');
      const shouldHidePresentButton =
        Boolean(hidePresentButton) || isMainAskAI || isFileAskAI || askAI;
      const presentToPublicDisabled = !permissions.hearingRoom.canPresent('public') || privateFile;
      const handlePresentToPublic = useCallback(() => {
        if (typeof presentViewOverrideHandler === 'function') {
          let overRidePage = '';
          if (btpActive) {
            overRidePage = `${globalPagePrefix}/${tab}/${currentPageNumber}`;
          } else {
            const globalPage = parseInt(currentFileMetaData.startPage) + currentPageNumber - 1;
            overRidePage = `${currentFileMetaData.globalPagePrefix}${globalPage}`;
          }
          presentViewOverrideHandler(currentFileMetaData, viewerControlRef, overRidePage);
        } else {
          present('public', isPresentViewer || false);
        }
      }, [
        btpActive,
        currentFileMetaData,
        currentPageNumber,
        globalPagePrefix,
        isPresentViewer,
        present,
        presentViewOverrideHandler,
        tab,
        viewerControlRef,
      ]);
      const presentToPublicButton = (
        <Tooltip title={T.translate('case.presentToPublicRoomTooltip')}>
          <CustomViewerButton
            icon={faChromecast}
            onClick={handlePresentToPublic}
            disabled={presentToPublicDisabled}
          />
        </Tooltip>
      );

      return (
        <div className="viewing-custom-viewer-toolbox">
          {((!isPreviewMode && !preview) || (isPreviewMode && copyable)) && (
            <>
              {!(isPreviewMode && copyable) && !readOnly && (
                <div className="document-options top-left">
                  <MouseToolSelectButtons
                    mouseTool={mouseTool}
                    selectedMouseTool={selectedMouseTool}
                    currentPageNumber={currentPageNumber}
                  />
                </div>
              )}
              {!(isPreviewMode && copyable) && !isFileToCompare && !shouldHidePresentButton && (
                <div className="document-options top-right">
                  {isTheatreMode ? (
                    <InfoText presenter={presenter} fileId={fileId} />
                  ) : isPresenting ? (
                    <TooltipWrapper isPresenting={isPresenting}>
                      <CustomViewerButton
                        icon={faChromecast}
                        icon2={presentMode === 'private' && faLock}
                        className={classnames({ present: isPresenting })}
                        onClick={() => {
                          if (!readOnly) present();
                        }}
                      />
                    </TooltipWrapper>
                  ) : (
                    !readOnly &&
                    (permissions.hearingRoom.canPresent('private') ||
                      permissions.hearingRoom.canPresent('public')) && (
                      <>
                        {isPresentModePage ? (
                          <>{presentToPublicButton}</>
                        ) : (
                          <>
                            {isTheatreMode ||
                              (!readOnly && (
                                <ClickAwayListener onClickAway={handleClickAway}>
                                  <div>
                                    <CustomViewerButton
                                      filterButton={true}
                                      icon={faChromecast}
                                      onClick={filterClicked}
                                    >
                                      <Badge
                                        badgeContent={filterCount}
                                        overlap="circular"
                                        color="primary"
                                        onClick={filterClicked}
                                      >
                                        <Avatar className="filterAnnotationsButton">
                                          <FilterListRoundedIcon style={{ color: 'gray' }} />
                                        </Avatar>
                                      </Badge>
                                    </CustomViewerButton>
                                    <Popper
                                      id={'simple-popover'}
                                      className="popper-shadow"
                                      open={!!filterPosition}
                                      anchorEl={filterPosition}
                                      disablePortal={true}
                                      placement="left-start"
                                      style={{ pointerEvents: 'all', width: '18rem' }}
                                    >
                                      <FilterAnnotations filterHandler={filterHandler} />
                                    </Popper>
                                  </div>
                                </ClickAwayListener>
                              ))}
                            {!readOnly && presentToPublicButton}
                          </>
                        )}
                      </>
                    )
                  )}
                </div>
              )}
              {/* When present button is hidden we still want the filter controls visible. */}
              {!(isPreviewMode && copyable) && !isFileToCompare && shouldHidePresentButton && (
                <div className="document-options top-right">
                  {isTheatreMode ||
                    (!readOnly && (
                      <ClickAwayListener onClickAway={handleClickAway}>
                        <div>
                          <CustomViewerButton
                            filterButton={true}
                            icon={faChromecast}
                            onClick={filterClicked}
                          >
                            <Badge badgeContent={filterCount} overlap="circular" color="primary">
                              <Avatar className="filterAnnotationsButton">
                                <FilterListRoundedIcon style={{ color: 'gray' }} />
                              </Avatar>
                            </Badge>
                          </CustomViewerButton>
                          <Popper
                            id={'simple-popover'}
                            className="popper-shadow"
                            open={!!filterPosition}
                            anchorEl={filterPosition}
                            disablePortal={true}
                            placement="left-start"
                            style={{ pointerEvents: 'all', width: '18rem' }}
                          >
                            <FilterAnnotations filterHandler={filterHandler} />
                          </Popper>
                        </div>
                      </ClickAwayListener>
                    ))}
                </div>
              )}
              {isPreviewMode && copyable ? (
                <div
                  className={classnames({ show: showLeftButtons }, 'document-options left')}
                  style={dynamicBottomStyle}
                >
                  <PageFitmentButton
                    zoomFitPage={() => zoom(ZoomType.Page)}
                    zoomFitWidth={() => zoom(ZoomType.Width)}
                  />
                </div>
              ) : (
                !readOnly && (
                  <div
                    className={classnames({ show: showLeftButtons }, 'document-options left')}
                    style={dynamicBottomStyle}
                  >
                    <CustomViewerButton icon={faPlus} onClick={() => zoom(ZoomType.In)} />
                    <CustomViewerButton icon={faMinus} onClick={() => zoom(ZoomType.Out)} />
                    <PageFitmentButton
                      zoomFitPage={() => zoom(ZoomType.Page)}
                      zoomFitWidth={() => zoom(ZoomType.Width)}
                    />
                  </div>
                )
              )}
              <div
                className="document-options right"
                style={{ flexDirection: 'row', ...(dynamicBottomStyle || {}) }}
              >
                {showExhibitNumber && (
                  <ExhibitNumber
                    currentFileMetaData={resolvedCurrentFileMetaData}
                    currentPageNumber={currentPageNumber}
                    fileId={fileId}
                    startPageForGlobalPaging={startPageForGlobalPaging}
                  />
                )}
                {shouldRenderGlobalPage && (
                  <CurrentGlobalPage
                    currentFileMetaData={resolvedCurrentFileMetaData}
                    currentPageNumber={currentPageNumber}
                    setPageNumber={setPageNumber}
                    fileId={fileId}
                    startPageForGlobalPaging={startPageForGlobalPaging}
                    readOnly={readOnly}
                    isFileToCompare={isFileToCompare}
                  />
                )}

                {!(isPreviewMode && copyable) && !readOnly && (
                  <CustomViewerMultiActionButton>
                    <CustomViewerButton icon={faRedo} onClick={() => rotate(90)} />
                    <CustomViewerButton icon={faUndo} onClick={() => rotate(-90)} />
                    {permissions.folders.canDownloadDocuments && (
                      <CustomViewerButton
                        icon={faDownload}
                        onClick={() => setShowFileVariantsModal(true)}
                      />
                    )}
                    <Tooltip title={T.translate('case.copyUrl')}>
                      <CustomViewerButton icon={faShareSquare} onClick={copyUrlHandler} />
                    </Tooltip>
                    <CustomViewerButton icon={faPlusSquare} onClick={() => {}} />
                  </CustomViewerMultiActionButton>
                )}
              </div>
            </>
          )}
          <div
            className={classnames({ show: showBottomButtons }, 'document-options toolbox')}
            style={dynamicBottomStyle}
          >
            {!readOnly && <CustomViewerButton icon={faFastBackward} onClick={goToFirstPage} />}
            {!readOnly && <CustomViewerButton icon={faStepBackward} onClick={goToPrevPage} />}
            {
              <PageNumberControl
                key={currentPageNumber}
                currentPageNumber={currentPageNumber}
                setPageNumber={setPageNumber}
                pageCount={pageCount}
                readOnly={readOnly}
              />
            }
            {!readOnly && <CustomViewerButton icon={faStepForward} onClick={goToNextPage} />}
            {!readOnly && <CustomViewerButton icon={faFastForward} onClick={goToLastPage} />}
          </div>
          {showFileVariantsModal && (
            <DownloadComponent
              show={showFileVariantsModal}
              selectedRows={[currentFileMetaData ? currentFileMetaData : {}]}
              handleClose={() => setShowFileVariantsModal(false)}
            />
          )}
        </div>
      );
    },
  ),
);
